import { appKey } from '@app/shared/brand'
import { X } from 'lucide-react'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useViewportBand } from '../lib/viewport'
import { Button } from './Button'
import { OverflowGroup } from './OverflowGroup'
import { usePageEndSlot } from './PageAction'
import { SearchField } from './SearchField'
import { Tooltip } from './Tooltip'
import './companion.css'
import './compopen.css'
import './comprail.css'

/** The width the person dragged the band to, remembered per browser. */
const WIDTH_KEY = appKey('companionWidth')

export type CompanionView = {
  /** Stable across renders — it is what "which view is open" is remembered as. */
  id: string
  label: string
  /**
   * Required, because a view that is not open is drawn as its icon alone — one
   * without would be a blank button. The label still names it in a tooltip and
   * to a screen reader; the icon is what a reader aims at.
   */
  icon: ReactNode
  /** How much this view holds, shown beside its name. Omitted when it is not a
   *  countable thing. Zero renders nothing: an empty count is noise. */
  count?: number
  /**
   * What to DRAW instead of the number, where the number is a bound.
   *
   * Kept apart from `count` because the two answer different questions: the
   * count decides whether the view is offered at all, and this decides what a
   * reader sees. A view that stopped counting at thirty still has to be
   * offered, so it needs a truthful count and an honest label — "30+" as a
   * number would be a lie and as the only value would break the rail.
   */
  countLabel?: string
  /**
   * Something in this view wants the reader — a font that will not print as
   * designed. The shut rail marks the view's icon with it, so the notice that
   * said so can be put away without the problem going quiet.
   */
  alert?: boolean
  /**
   * The view fills the band and manages its own scrolling and its own foot —
   * the assistant, whose composer sits at the bottom of the band the way a
   * workspace's commit does. A reading view takes the band's padding instead.
   */
  flush?: boolean
  /**
   * The narrowest this view can be read at, in px.
   *
   * The band widens to it while the view is open and returns to the person's
   * own width when they leave — so a view with a shape to keep is not asking
   * them to find the right width for it, and a width they chose for something
   * else is not taken away from them.
   */
  roomFor?: number
  /**
   * The width this view reads best at, in px, used until the person sets one.
   *
   * The band's own default is sized for reading — the assistant, a contents.
   * A narrow list beside work that needs the width, a room plan, asks for less;
   * once the person drags the band, their width is the answer for every view.
   */
  prefers?: number
  /**
   * A field over the view, drawn by the BAND rather than by the view.
   *
   * Every list beside a page is searched the same way, so there is one field in
   * one place: it spans the band's head, above whatever is open, and a view
   * that offers searching declares the query instead of building a strip of its
   * own. Three views each pinning their own field is three fields at three
   * heights, and the one a reader learns is whichever they met first.
   *
   * Drawn outside the scrolling body, so it is pinned by where it sits rather
   * than by a sticky rule each view has to remember.
   */
  search?: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    ariaLabel?: string
  }
  /**
   * A second way to cut the same list — the comments' open-or-all.
   *
   * Beside the field where there is one, and alone where there is not: cutting
   * a list is not a property of a text box, and a view that wants one way in
   * should not have to invent the other to get it. It trails the field because
   * the field is what a reader reaches for.
   */
  filter?: ReactNode
  /**
   * A control the view owns that is not a way to cut its list.
   *
   * Kept apart from `filter` because the two are different kinds of thing and
   * the name should say which: one narrows what is shown, this changes
   * something about the view or what it reports. They share the row because a
   * view gets one row of chrome, not one per kind of control.
   */
  tools?: ReactNode
  /**
   * Where a document's two bars stand either side of it, a view that navigates
   * the document itself — a book's contents — is drawn in the left bar rather
   * than here. Elsewhere the band draws it like any other view.
   */
  place?: 'nav'
  render: () => ReactNode
}

/**
 * Something the strip DOES, as against something it opens.
 *
 * A view changes what is beside the page; an action acts on the page. They are
 * declared apart and drawn apart — divided by a rule on the rail, set after the
 * views in the head — because folding them into one list would offer a control
 * that opens nothing among controls that do.
 */
export type CompanionAction = {
  id: string
  label: string
  icon: ReactNode
  onSelect: () => void
}

/**
 * The band while it is shut: its own strip, with the labels dropped.
 *
 * The views are NAVIGATION rather than chrome, so at this width they do not
 * collapse to a single trigger. Collapsed to one, that trigger has to be named
 * after one of them — which is a control that says "Notes" over a band also
 * holding five open comments, and a reader who never opens it never learns the
 * rest is there. The counts are the reason the shut state is worth drawing.
 *
 * That reasoning is about an edge with room for the several. Below the roomy
 * edge the band covers the page rather than standing beside it, and the shut
 * state is `CompanionOpener` — one entry point, named for the view it opens,
 * onto a strip that then carries the rest.
 *
 * Same list and same order as the strip, off the same declaration, so what is
 * on offer cannot differ between shut and open.
 *
 * A view with nothing in it is not drawn: an entry point onto nothing teaches
 * that it does nothing. A view that has no count to give — sources, which are
 * a list rather than a tally — is always drawn, since absent a number its
 * emptiness is not knowable from here.
 *
 * **It names the control that stands for it where it is hidden.** Below the
 * roomy edge the rail is not drawn, and a group of controls that goes with
 * nothing declared in its place reads, to anything checking reach, as several
 * controls nobody can get to. What stands in is the opener's entry, which is
 * named for the first view offered — the same rule, read off the same list, so
 * the word here cannot differ from the word there.
 */
export function CompanionRail({
  views,
  actions = [],
  onOpen,
}: {
  views: CompanionView[]
  /**
   * Things the strip DOES, below the things it opens.
   *
   * A surface with no bar of its own to carry a page action is what asks for
   * this: on a public reading copy the strip is the only chrome there is, and a
   * second control floating beside it is the clutter the strip exists to
   * prevent.
   */
  actions?: CompanionAction[]
  onOpen: (id: string) => void
}) {
  const offered = views.filter((v) => v.count === undefined || v.count > 0)
  if (!offered.length && !actions.length) return null

  return (
    <nav
      className="comprail"
      aria-label="What is beside this page"
      data-opens={offered[0]?.label}
    >
      {offered.map((v) => (
        <Tooltip key={v.id} label={v.label}>
          <button
            type="button"
            className="comprail__view"
            aria-label={
              v.count ? `${v.label} — ${v.count}` : `${v.label}` /* said once */
            }
            onClick={() => onOpen(v.id)}
          >
            {v.icon}
            {v.count ? (
              <span className="comprail__count">{v.countLabel ?? v.count}</span>
            ) : null}
          </button>
        </Tooltip>
      ))}
      {actions.map((a) => (
        <Tooltip key={a.id} label={a.label}>
          <button
            type="button"
            className="comprail__view"
            aria-label={a.label}
            onClick={a.onSelect}
          >
            {a.icon}
          </button>
        </Tooltip>
      ))}
    </nav>
  )
}

/**
 * The shut band below the roomy edge: ONE entry point, not the rail's several.
 *
 * The rail's refusal to collapse holds where the band sits BESIDE the page — an
 * edge with room for five cells, each carrying its own count. Below the roomy
 * edge the band covers the page instead, and its own head strip then carries
 * every view with the active one named, so moving between them is already
 * answered. What is left for the shut state to say is that there is something
 * beside the book at all.
 *
 * One control is also what keeps the shut state off a count of views: a sixth
 * widens the strip inside the band and changes nothing here, where six icons in
 * a row of equal cells would have to be re-fitted.
 *
 * It opens the first view offered and is NAMED FOR THAT VIEW — the contents,
 * wherever a book has parts, because what the artifact IS leads the band. A
 * name of its own for the band would take a word a reader has no name for, and
 * a fixed word would be a lie on the book that has no parts: there the contents
 * is not offered at all, and a cell reading "Contents" would open the notes.
 * The rest is named by the band's own strip the moment it opens.
 *
 * It carries NO count. A band holds notes, remarks and events, which are
 * different kinds of thing — summed they make a number that names none of them,
 * and it would read as a count of the one view the button is named after. Each
 * count still stands on its own view inside the band, where it says what it is
 * counting.
 */
export function CompanionOpener({
  views,
  actions = [],
  onOpen,
}: {
  views: CompanionView[]
  /**
   * What the strip DOES, beside the one thing it opens. A surface with no bar of
   * its own to carry a page action puts it here, and it must not be lost to the
   * width — this row is the only chrome a public reading copy has.
   */
  actions?: CompanionAction[]
  onOpen: (id: string) => void
}) {
  const band = useViewportBand()
  const slot = usePageEndSlot()

  const offered = views.filter((v) => v.count === undefined || v.count > 0)
  const first = offered[0]

  if (!first && !actions.length) return null

  /*
   * At the roomy edge and above it is not offered, so it is not in the page.
   *
   * What stands beside the page there is the surface's own: a rail of cells, or
   * a contents column. Left in the page and hidden, this is a control by a name
   * nothing on screen answers to — the rail's cell for a view with a count
   * reads `Notes — 3`, and a contents column holds entries and no control
   * called Contents at all.
   */
  if (band === 'roomy') return null

  const cell = (
    <>
      {first ? (
        <button
          type="button"
          className="compopen__entry"
          onClick={() => onOpen(first.id)}
        >
          {first.icon}
          <span className="compopen__word">{first.label}</span>
        </button>
      ) : null}
      {actions.length ? (
        /*
         * The actions collapse WHOLE, and the opener beside them does not.
         *
         * They are chrome — things done to the book — so they answer a narrow
         * row the way every other group of chrome does: one entry point once
         * they stop fitting, measured rather than counted, so a third and a
         * fourth cannot change the shape of the row. The opener is navigation
         * and keeps its cell, which is the same division the shell's own row
         * makes between a list's tools and a page's actions.
         */
        <OverflowGroup label="Actions" short="Actions" align="end">
          {actions.map((a) => (
            <button
              key={a.id}
              type="button"
              className="compopen__entry"
              aria-label={a.label}
              onClick={a.onSelect}
            >
              {a.icon}
              <span className="compopen__word">{a.label}</span>
            </button>
          ))}
        </OverflowGroup>
      ) : null}
    </>
  )

  /*
   * The shell already draws one row beneath the header at handheld, and the
   * responsive strategy is explicit that everything a screen offers goes in it
   * rather than in a row of its own. So where that row exists this becomes a
   * cell of it — at its trailing end, the edge the band itself comes from.
   *
   * `TableHeader` makes the same test for the same reason: CSS cannot move a
   * node into another container, and a second copy would be a second control.
   */
  if (band === 'handheld' && slot !== null) return createPortal(cell, slot)

  /*
   * Everywhere else below the roomy edge — the widths between the handheld edge
   * and the roomy one, and a public reading copy at every width, which has no
   * shell to portal into — the page carries the row itself. It sticks, because
   * the contents is reached mid-read and an opener that left with the first
   * screenful would mean scrolling back for it.
   */
  return <div className="compopen">{cell}</div>
}

/**
 * What is beside the page rather than on it.
 *
 * A book's contents, its notes, its sources, what is said about it and the
 * assistant are one kind of thing, so they share one band and one strip of
 * width. A band each would be several bands competing for the same edge, and
 * the page squeezed by however many happened to be open.
 *
 * The band is a Companion, not a Sheet, and the test is whether the work
 * continues underneath: reading does. You keep the notes open while you read
 * the chapter they belong to, which is the entire justification for taking
 * width instead of covering.
 */
export function Companion({
  views,
  actions = [],
  active,
  onActiveChange,
  onClose,
  lead,
}: {
  views: CompanionView[]
  /**
   * Things the band DOES, in its head beside the close.
   *
   * The same declaration the rail takes, so an action stays in one place as the
   * band opens and shuts rather than moving between two — a surface with no bar
   * of its own carries its page's actions on this strip, and a control that
   * vanished when a view opened would be one you had to close something to
   * reach.
   */
  actions?: CompanionAction[]
  /** Which view is open. Controlled, because the page opens it at one. */
  active: string
  onActiveChange: (id: string) => void
  onClose: () => void
  /**
   * What heads the band beside an open document: who is here, the document's
   * actions, the account and the way back to the rail. The close is the
   * lead's, so the band draws none of its own.
   */
  lead?: ReactNode
}) {
  const labelId = useId()
  const [width, setWidthRaw] = useState(
    () => Number(localStorage.getItem(WIDTH_KEY)) || 0,
  )
  const dragging = useRef(false)

  // Remembered on every change, not only on pointer-up: a width set from the
  // keyboard is a width somebody chose just as deliberately.
  const setWidth = useCallback((w: number) => {
    setWidthRaw(w)
    localStorage.setItem(WIDTH_KEY, String(Math.round(w)))
  }, [])

  /*
   * Escape closes it, at every width.
   *
   * Beside the page that is a convenience. Covering the page it is the way out:
   * there the band is the whole window, and on a public reading copy — which
   * re-points `--topbar-h` to nothing because it has no bar — it reaches the top
   * of the screen, leaving the ✕ in its own head as the single exit. One key
   * away is what every other surface in the app offers.
   */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      /*
       * The band is the OUTERMOST thing this key means, so it goes last: it
       * closes only where nothing nearer has claimed the press. Nearer things
       * are three, and each would otherwise take the band with it —
       *
       *  · a menu or listbox, which marks the key handled;
       *  · a surface standing over the band, whose own Escape is the one meant;
       *  · a field being typed in, where Escape clears what was typed. That is
       *    the search in this band's own head, so closing on it would mean
       *    narrowing a list and dismissing the list in one press.
       *
       * Emptying the field leaves nothing nearer to claim it, and the next
       * press reaches here.
       */
      if (e.defaultPrevented) return
      if (document.querySelector('.sheet-overlay, [role="dialog"]')) return
      const on = e.target
      if (on instanceof HTMLInputElement || on instanceof HTMLTextAreaElement) {
        if (on.value) return
      }
      onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    function move(e: PointerEvent) {
      if (!dragging.current) return
      // Bounded at both ends: narrower and what the band holds cannot be read,
      // wider and the page it is meant to sit beside stops being one.
      setWidth(
        Math.min(
          Math.max(window.innerWidth - e.clientX, 340),
          Math.max(window.innerWidth * 0.7, 480),
        ),
      )
    }
    function up() {
      if (!dragging.current) return
      dragging.current = false
      document.body.classList.remove('is-resizing')
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    // Bound once: `setWidth` is stable and the handlers read the pointer rather
    // than React state. Re-subscribing per pixel is how a resize stutters.
  }, [setWidth])

  const current = views.find((v) => v.id === active) ?? views[0]

  /*
   * A view may ask for more room than the band is set to, and gets it.
   *
   * The person's own width is never overwritten — it is what the band returns
   * to the moment they open something else, and it is what the handle still
   * sets. This only refuses to go NARROWER than a view says it can be read at,
   * which is the difference between the band having a width and a view being
   * unreadable in it: a list of acts against a column of times has a measure
   * below which the two collide, and a reader who dragged the band narrow for
   * a contents did not decide that.
   */
  const asked = Math.max(width || current?.prefers || 0, current?.roomFor ?? 0)

  return (
    <aside
      className="comp"
      {...(views.length
        ? { 'aria-labelledby': labelId }
        : { 'aria-label': 'Tools' })}
      style={asked && views.length ? { width: `${asked}px` } : undefined}
    >
      {/* A separator, operated by pointer or by its own arrow keys — the split
          is the person's to set either way. */}
      {views.length === 0 ? null : (
        <div
          className="comp__resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the panel"
          aria-valuenow={Math.round(width)}
          aria-valuemin={340}
          aria-valuemax={900}
          tabIndex={0}
          onPointerDown={(e) => {
            dragging.current = true
            document.body.classList.add('is-resizing')
            e.preventDefault()
          }}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 64 : 16
            const from =
              width || (e.currentTarget.parentElement?.clientWidth ?? 0)
            if (e.key === 'ArrowLeft') setWidth(Math.min(from + step, 900))
            else if (e.key === 'ArrowRight')
              setWidth(Math.max(from - step, 340))
            else return
            e.preventDefault()
          }}
        />
      )}

      {lead ? <div className="comp__lead">{lead}</div> : null}

      {views.length === 0 ? null : (
        <>
          <div className="comp__head">
            <div className="comp__views" role="tablist" id={labelId}>
              {views.map((v) => {
                const active = v.id === current?.id
                /*
                 * The OPEN view says its name; the rest are the icon alone.
                 *
                 * A strip of five words is wider than the band at its narrower
                 * settings, and a tab strip is navigation you read — so it must not
                 * clip. Naming only the current one keeps the whole strip legible
                 * at any width and states the one thing a reader needs without
                 * pointing at anything: where they are.
                 *
                 * The others carry their name in a tooltip, which is the same
                 * treatment the header gives an icon-only action, and the button
                 * keeps its accessible name either way — so nothing is lost to a
                 * screen reader by a word being hidden from the eye.
                 */
                const tab = (
                  <button
                    key={v.id}
                    type="button"
                    role="tab"
                    className="comp__view"
                    aria-selected={active}
                    aria-label={active ? undefined : v.label}
                    {...(active ? { 'data-active': '' } : {})}
                    onClick={() => onActiveChange(v.id)}
                  >
                    {v.icon}
                    {active ? v.label : null}
                    {/* Zero renders nothing — an empty count is noise, and its
                    absence already says the view is empty. */}
                    {v.count ? (
                      <span className="comp__count">
                        {v.countLabel ?? v.count}
                      </span>
                    ) : null}
                  </button>
                )
                return active ? (
                  tab
                ) : (
                  <Tooltip key={v.id} label={v.label}>
                    {tab}
                  </Tooltip>
                )
              })}
            </div>
            {/* Before the close, which is the last thing on the strip because it is
            what ends the band rather than something it offers. */}
            {actions.map((a) => (
              <Tooltip key={a.id} label={a.label}>
                <Button
                  variant="quiet"
                  icon
                  className="comp__action"
                  aria-label={a.label}
                  onClick={a.onSelect}
                >
                  {a.icon}
                </Button>
              </Tooltip>
            ))}
            {lead ? null : (
              <Tooltip label="Close the panel">
                <Button
                  variant="quiet"
                  icon
                  className="comp__close"
                  aria-label="Close the panel"
                  onClick={onClose}
                >
                  <X size={16} />
                </Button>
              </Tooltip>
            )}
          </div>

          {/* The one field, over whatever is open. It spans the head because what it
          narrows spans the band: a field set to a modest measure leaves a strip
          of empty band beside it and reads as a tool of the first thing under
          it rather than of the view. */}
          {current?.search || current?.filter || current?.tools ? (
            <div className="comp__search">
              {current.search ? (
                <SearchField
                  value={current.search.value}
                  onChange={current.search.onChange}
                  placeholder={current.search.placeholder}
                  ariaLabel={current.search.ariaLabel ?? 'Search'}
                />
              ) : null}
              {current.filter}
              {current.tools}
            </div>
          ) : null}

          <div
            className="comp__body"
            role="tabpanel"
            {...(current?.flush ? { 'data-flush': '' } : {})}
          >
            {current?.render()}
          </div>
        </>
      )}
    </aside>
  )
}
